import React, { useState } from 'react';
import { api, ApiError } from '../api';
import { Badge, Button, Card, CardBody, RiskBadge } from '../design/primitives';
import { useStore } from '../state/store';

/**
 * One bank operation, rendered as a form.
 *
 * The expected verdict is shown up front. Hiding it would make the demo more
 * dramatic and less honest: someone about to click "Bulk export customer data"
 * should know beforehand that this is the action the bank refuses, because the
 * point being made is about the boundary, not about surprising them.
 */
export function OperationCard({ tool, hostId }: { tool: any; hostId?: string }) {
  const { focusTrace } = useStore();
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries((tool.params ?? []).map((param: any) => [param.name, param.defaultValue ?? ''])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const outcome = await api.operate({
        toolName: tool.name,
        args: coerce(tool.params ?? [], values),
        hostId,
      });
      focusTrace(outcome.traceId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That operation could not be started.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardBody>
        <form onSubmit={submit}>
          <div className="mb-op__head">
            <div className="mb-grow">
              <h3>{tool.title}</h3>
              <p className="mb-op__desc">{tool.description}</p>
            </div>
            <RiskBadge level={tool.riskLevel} />
          </div>

          <div className="mb-op__tags">
            <Badge tone="neutral" glyph={false}>
              {tool.name}
            </Badge>
            {tool.expectedVerdict === 'block' && <Badge tone="block">Expected: refused</Badge>}
            {tool.expectedVerdict === 'require_approval' && <Badge tone="approval">Expected: held</Badge>}
          </div>

          {(tool.params ?? []).map((param: any) => (
            <ParamField
              key={param.name}
              toolName={tool.name}
              param={param}
              value={values[param.name]}
              onChange={value => setValues(current => ({ ...current, [param.name]: value }))}
            />
          ))}

          {error && (
            <p className="mb-op__error" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="sm" disabled={busy}>
            {busy ? 'Running…' : 'Run operation'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}

function ParamField({
  toolName,
  param,
  value,
  onChange,
}: {
  toolName: string;
  param: any;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const id = `${toolName}-${param.name}`;
  const helpId = param.help ? `${id}-help` : undefined;
  const shared = {
    id,
    required: param.required,
    placeholder: param.placeholder,
    value: String(value ?? ''),
    'aria-describedby': helpId,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      onChange(event.target.value),
  };

  return (
    <div className="mb-field">
      <label className="mb-field__label" htmlFor={id}>
        {param.label}
        {param.required && <span aria-hidden> *</span>}
      </label>

      {param.type === 'select' ? (
        <select className="mb-select" {...shared}>
          {(param.options ?? []).map((option: any) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : param.type === 'textarea' ? (
        <textarea className="mb-textarea" {...shared} />
      ) : (
        <input
          className={`mb-input${param.type === 'iban' ? ' mb-input--mono' : ''}`}
          type={param.type === 'number' || param.type === 'currency' ? 'number' : 'text'}
          {...shared}
        />
      )}

      {param.help && (
        <span className="mb-field__help" id={helpId}>
          {param.help}
        </span>
      )}
    </div>
  );
}

/** Inputs hand back strings; policy constraints compare numerically. */
function coerce(params: any[], values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const param of params) {
    const raw = values[param.name];
    if (raw === '' || raw === undefined) continue;
    out[param.name] = param.type === 'number' || param.type === 'currency' ? Number(raw) : raw;
  }
  return out;
}
